import React, { useMemo } from "react"
import * as Yup from "yup"
import { Form, Formik, FormikHelpers, FormikProps } from "formik"
import { useQuery } from "react-query"
import { AxiosError } from "axios"
import { toast } from "react-toastify"

// --- Composants & Champs ---
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyAlert from "@common-utils/MyAlert"
import MySelectField from "@common-utils/fields/MySelectField"
import MyInputField from "@common-utils/fields/MyInputField"
import MyEditorField from "@common-utils/fields/MyEditorField"
import MyFileField from "@common-utils/fields/MyFileField"
import MySelectGetAllDataFromServer from "@common-utils/fields/MySelectGetAllDataFromServer"

// --- Modèles & Constantes ---
import {
     CONST_CANDIDAT_REMUNERATION_SOUHAITE_BRUT_NET_OPTIONS,
     CONST_CANDIDAT_REMUNERATION_SOUHAITE_JOUR_OR_MOIS_OR_ANNEE_OPTIONS,
     CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_CLIENT,
     CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_TELEPHONIQUE,
     CONST_DISPONIBILITE_DATE_EXACTE,
     CONST_DISPONIBILITE_OPTIONS_FOR_SELECT2,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_MOBILITE_DEPARTEMENTAL,
     CONST_MOBILITE_OPTIONS_FOR_SELECT2,
     CONST_MOBILITE_PAR_VILLE,
     CONST_MOBILITE_REGIONAL,
} from "@common-constants/*"
import { CONST_API_UTILS_GET_CLIENTS_ENDPOINT, CONST_API_UTILS_GET_REGIONS_URL_ENDPOINT } from "../../../../endpoints"
import { ICandidatSuiviEditFormikModel, ICandidatSuiviEditFormPropsModel, ICandidatSuiviEditRequestModel } from "./core/_models"
import { getCandidatSuivi, setCandidatSuivi } from "./core/_requests"
import { ICandidatSuiviModel, IHttpErrorResponseModel } from "@common-models/*"
import MySelectGetAllDataFromServerFormField from "@common-components/fields/MySelectGetAllDataFromServerFormField"
import MyCheckboxFormField from "@common-components/fields/MyCheckboxFormField"
import MyDateDayMonthYearFormField from "@common-components/fields/MyDateDayMonthYearFormField"

const REACT_QUERY_KEY_GET_CANDIDAT_SUIVI_DETAILS_QUERY = "REACT_QUERY_KEY_GET_CANDIDAT_SUIVI_DETAILS_QUERY"

const CandidatSuiviEditForm = ({ id, handleSubmitCallBack }: ICandidatSuiviEditFormPropsModel) => {
     // 1. Récupération des données
     const detailsQuery = useQuery<ICandidatSuiviModel, AxiosError>([REACT_QUERY_KEY_GET_CANDIDAT_SUIVI_DETAILS_QUERY, id], async () => {
          try {
               const r = await getCandidatSuivi(id)
               return r.data
          } catch (e: any) {
               toast.error(e.response?.data?.detail, { autoClose: false })
               throw e
          }
     })

     const isPhoneInterview = detailsQuery.data?.type === CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_TELEPHONIQUE
     const isClientInterview = detailsQuery.data?.type === CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_CLIENT

     // 2. Schéma de Validation
     const validationSchema = useMemo(() => {
          return Yup.object().shape({
               notes: Yup.string().label("Notes").nullable(),
               // Le client est requis si c'est un entretien client
               client: Yup.number()
                    .nullable()
                    .when([], {
                         is: () => isClientInterview,
                         then: s => s.required("Le client est obligatoire"),
                    }),
               disponibilite: Yup.string().label("Disponibilité").nullable(),
               disponibiliteDate: Yup.string()
                    .when("disponibilite", { is: CONST_DISPONIBILITE_DATE_EXACTE, then: s => s.required() })
                    .label("Date de disponibilité")
                    .nullable(),
               mobilite: Yup.string().label("Mobilité").nullable(),
               mobiliteRegions: Yup.array()
                    .when("mobilite", { is: CONST_MOBILITE_REGIONAL, then: s => s.required() })
                    .nullable(),
               mobiliteDepartements: Yup.array()
                    .when("mobilite", { is: CONST_MOBILITE_DEPARTEMENTAL, then: s => s.required() })
                    .nullable(),
               mobiliteVilles: Yup.array()
                    .when("mobilite", { is: CONST_MOBILITE_PAR_VILLE, then: s => s.required() })
                    .nullable(),
               remunerationSouhaiteMontant: Yup.number().label("Rémunération souhaité").nullable(),
               remunerationSouhaiteFormat: Yup.string().label("Rémunération brut ou net").nullable(),
               remunerationSouhaiteJourOrMensuelOrAnnuel: Yup.string().label("Rémunération jour / mois / année").nullable(),
               voulezVousAjouterOuRemplacerDocument: Yup.boolean(),
               document: Yup.mixed()
                    .when("voulezVousAjouterOuRemplacerDocument", { is: true, then: s => s.required() })
                    .test("fileSize", "Max 10MB", (v: any) => !v || v.size <= 10485760)
                    .nullable(),
          })
     }, [isClientInterview])

     // 3. Valeurs Initiales
     const initialValues: ICandidatSuiviEditFormikModel = useMemo(() => {
          const data = detailsQuery.data
          const smd = data?.candidatSuiviSMDs?.[0]

          return {
               notes: data?.commentaire || "",
               client: data?.type === CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_CLIENT ? data.client?.id ?? null : null,
               disponibilite: isPhoneInterview && smd ? smd.disponibilite : null,
               disponibiliteDate: isPhoneInterview && smd ? smd.disponibiliteDate?.format("YYYY-MM-DD") : null,
               mobilite: isPhoneInterview && smd ? smd.mobilite : null,
               mobiliteRegions: isPhoneInterview && smd ? smd.candidatSuiviSMDRegions.map(i => i.region?.id as number) : [],
               mobiliteDepartements: isPhoneInterview && smd ? smd.candidatSuiviSMDDepartements.map(i => i.departement?.id as number) : [],
               mobiliteVilles: isPhoneInterview && smd ? smd.candidatSuiviSMDVilles.map(i => i.ville?.id as number) : [],
               remunerationSouhaiteMontant: isPhoneInterview && smd ? smd.remunerationSouhaiteMontant : null,
               remunerationSouhaiteFormat: isPhoneInterview && smd ? smd.remunerationSouhaiteFormat : null,
               remunerationSouhaiteJourOrMensuelOrAnnuel: isPhoneInterview && smd ? smd.remunerationSouhaiteJourOrMensuelOrAnnuel : null,
               voulezVousAjouterOuRemplacerDocument: false,
               document: null,
          }
     }, [detailsQuery.data, isPhoneInterview])

     // 4. Soumission
     const handleSubmit = (values: ICandidatSuiviEditFormikModel, helpers: FormikHelpers<ICandidatSuiviEditFormikModel>) => {
          helpers.setStatus(null)

          const requestModel: ICandidatSuiviEditRequestModel = {
               notes: values.notes,
               document: values.document,
               ...(isPhoneInterview
                    ? {
                           disponibilite: values.disponibilite,
                           disponibiliteDate: values.disponibiliteDate,
                           mobilite: values.mobilite,
                           mobiliteRegions: values.mobiliteRegions,
                           mobiliteDepartements: values.mobiliteDepartements,
                           mobiliteVilles: values.mobiliteVilles,
                           remunerationSouhaiteMontant: values.remunerationSouhaiteMontant,
                           remunerationSouhaiteFormat: values.remunerationSouhaiteFormat,
                           remunerationSouhaiteJourOrMensuelOrAnnuel: values.remunerationSouhaiteJourOrMensuelOrAnnuel,
                      }
                    : {}),
               ...(isClientInterview ? { client: values.client } : {}),
          }

          setCandidatSuivi(id, requestModel)
               .then(r => {
                    handleSubmitCallBack(r.data)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         Object.keys(error.errors).forEach(key => helpers.setFieldError(key, error.errors[key]))
                    }
                    helpers.setStatus(error?.detail || "Une erreur est survenue")
                    helpers.setSubmitting(false)
               })
     }

     if (detailsQuery.isLoading)
          return (
               <div className="text-center p-10">
                    <MySimpleSpinner size="xl" />
               </div>
          )
     if (detailsQuery.error) return <MyAlert type="danger">{detailsQuery.error.response?.data.detail}</MyAlert>

     return (
          <Formik<ICandidatSuiviEditFormikModel> initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema} enableReinitialize>
               {(fProps: FormikProps<ICandidatSuiviEditFormikModel>) => {
                    const { values, errors, setFieldValue, status, isSubmitting } = fProps

                    return (
                         <Form noValidate autoComplete="off" className="d-flex flex-column gap-5">
                              {status && <MyAlert type="danger">{status}</MyAlert>}

                              {/* --- SECTION 0: CLIENT (Entretien Client Uniquement) --- */}
                              {isClientInterview && (
                                   <div className="card shadow-none border border-gray-300">
                                        <div className="card-header min-h-auto px-4 py-2 bg-light">
                                             <div className="card-title fs-6 fw-bold text-gray-700">Client concerné</div>
                                        </div>
                                        <div className="card-body p-4">
                                             <MySelectGetAllDataFromServer
                                                  name={"client"}
                                                  value={values.client}
                                                  defaultOptions={
                                                       detailsQuery.data?.client && [
                                                            { value: detailsQuery.data?.client.id as number, label: detailsQuery.data?.client.nom as string },
                                                       ]
                                                  }
                                                  onChange={val => setFieldValue("client", val)}
                                                  url={CONST_API_UTILS_GET_CLIENTS_ENDPOINT}
                                                  isInvalid={!!errors.client}
                                                  placeholder="Rechercher un client..."
                                             />
                                             {errors.client && <div className="text-danger mt-1">{errors.client}</div>}
                                        </div>
                                   </div>
                              )}

                              {/* --- SECTION 1: NOTES --- */}
                              <div className="card shadow-none border border-gray-300">
                                   <div className="card-header min-h-auto px-4 py-2 bg-light">
                                        <div className="card-title fs-6 fw-bold text-gray-700">Notes du suivi</div>
                                   </div>
                                   <div className="card-body p-4">
                                        <MyEditorField
                                             value={values.notes || ""}
                                             height={200}
                                             onChange={val => setFieldValue("notes", val)}
                                             placeholder="Rédigez vos notes ici..."
                                        />
                                        {errors.notes && <div className="text-danger mt-1">{errors.notes}</div>}
                                   </div>
                              </div>

                              {/* --- SECTION 2: DMS (Entretien Téléphonique Uniquement) --- */}
                              {isPhoneInterview && (
                                   <div className="card shadow-none border border-gray-300">
                                        <div className="card-header min-h-auto px-4 py-2 bg-light">
                                             <div className="card-title fs-6 fw-bold text-gray-700">Détails D.M.S</div>
                                        </div>
                                        <div className="card-body p-4">
                                             <div className="row g-5">
                                                  <div className="col-md-6">
                                                       <div className="mb-4">
                                                            <label className="form-label fw-bold required">Disponibilité</label>
                                                            <MySelectField
                                                                 name="disponibilite"
                                                                 options={CONST_DISPONIBILITE_OPTIONS_FOR_SELECT2}
                                                                 value={values.disponibilite}
                                                                 onChange={v => setFieldValue("disponibilite", v)}
                                                                 isSearchable={false}
                                                                 isInvalid={false}
                                                            />
                                                            {values.disponibilite === CONST_DISPONIBILITE_DATE_EXACTE && (
                                                                 <div className="mt-4">
                                                                      <MyDateDayMonthYearFormField
                                                                           name="disponibiliteDate"
                                                                           formikProps={fProps}
                                                                           placeholder="Choisissez une date"
                                                                           label={<span className={"required"}>Date de disponibilité</span>}
                                                                      />
                                                                 </div>
                                                            )}
                                                       </div>
                                                       <div>
                                                            <label className="form-label fw-bold">Rémunération souhaitée</label>
                                                            <div className="mb-2">
                                                                 <MySelectField
                                                                      name={"remunerationSouhaiteFormat"}
                                                                      placeholder={"Base (Brut / Net / HT)"}
                                                                      options={CONST_CANDIDAT_REMUNERATION_SOUHAITE_BRUT_NET_OPTIONS}
                                                                      value={values.remunerationSouhaiteFormat}
                                                                      isInvalid={!!errors.remunerationSouhaiteFormat}
                                                                      onChange={val => setFieldValue("remunerationSouhaiteFormat", val)}
                                                                      isSearchable={false}
                                                                 />
                                                            </div>
                                                            <div className="mb-2">
                                                                 <MySelectField
                                                                      name={"remunerationSouhaiteJourOrMensuelOrAnnuel"}
                                                                      options={CONST_CANDIDAT_REMUNERATION_SOUHAITE_JOUR_OR_MOIS_OR_ANNEE_OPTIONS}
                                                                      placeholder={"Fréquence (Par jour / mois / année)"}
                                                                      value={values.remunerationSouhaiteJourOrMensuelOrAnnuel}
                                                                      isInvalid={!!errors.remunerationSouhaiteJourOrMensuelOrAnnuel}
                                                                      onChange={val => setFieldValue("remunerationSouhaiteJourOrMensuelOrAnnuel", val)}
                                                                      isSearchable={false}
                                                                 />
                                                            </div>
                                                            <div className="input-group mb-2">
                                                                 <MyInputField
                                                                      name="remunerationSouhaiteMontant"
                                                                      value={values.remunerationSouhaiteMontant}
                                                                      onChange={v => setFieldValue("remunerationSouhaiteMontant", v)}
                                                                      type="number"
                                                                      isInvalid={false}
                                                                      placeholder={"Montant"}
                                                                 />
                                                                 <span className="input-group-text">€</span>
                                                            </div>
                                                       </div>
                                                  </div>
                                                  <div className="col-md-6">
                                                       <div className="mb-4">
                                                            <label className="form-label fw-bold required">Mobilité</label>
                                                            <MySelectField
                                                                 name="mobilite"
                                                                 options={CONST_MOBILITE_OPTIONS_FOR_SELECT2}
                                                                 value={values.mobilite}
                                                                 onChange={v => setFieldValue("mobilite", v)}
                                                                 isSearchable={false}
                                                                 isInvalid={false}
                                                            />
                                                       </div>
                                                       {values.mobilite === CONST_MOBILITE_REGIONAL && (
                                                            <MySelectGetAllDataFromServerFormField
                                                                 name="mobiliteRegions"
                                                                 endpoint={CONST_API_UTILS_GET_REGIONS_URL_ENDPOINT}
                                                                 method="GET"
                                                                 formikProps={fProps}
                                                                 isMulti
                                                                 placeholder="Sélectionnez les régions"
                                                                 defaultOptions={(detailsQuery.data?.candidatSuiviSMDs?.[0]?.candidatSuiviSMDRegions || []).map(item => ({
                                                                      label: item.region?.nom as string,
                                                                      value: item.region?.id as number,
                                                                 }))}
                                                                 label={<span className={"required"}>Régions</span>}
                                                            />
                                                       )}
                                                       {/* (Autres types de mobilité...) */}
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              )}

                              {/* --- SECTION 3: DOCUMENTS --- */}
                              <div className="bg-light-primary rounded p-4 border border-primary border-dashed">
                                   <MyCheckboxFormField
                                        name="voulezVousAjouterOuRemplacerDocument"
                                        formikProps={fProps}
                                        text={
                                             <span className="fw-bold text-gray-800 ms-2">
                                                  {detailsQuery.data?.candidatSuiviDocuments?.length ? "Remplacer le document existant ?" : "Joindre un document ?"}
                                             </span>
                                        }
                                   />
                                   {values.voulezVousAjouterOuRemplacerDocument && (
                                        <div className="animate__animated animate__fadeIn mt-3">
                                             <MyFileField name="document" onChange={v => setFieldValue("document", v)} isInvalid={!!errors.document} />
                                             <div className="form-text text-muted">Formats acceptés: PDF, Word. Max 10Mo.</div>
                                             {errors.document && <div className="text-danger small">{errors.document as string}</div>}
                                        </div>
                                   )}
                              </div>

                              <div className="d-flex justify-content-end pt-5">
                                   <button className="btn btn-primary" type="submit" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                             <>
                                                  <MySimpleSpinner size="sm" />
                                                  <span className="ms-2">Enregistrement...</span>
                                             </>
                                        ) : (
                                             "Enregistrer les modifications"
                                        )}
                                   </button>
                              </div>
                         </Form>
                    )
               }}
          </Formik>
     )
}

export default CandidatSuiviEditForm
